import { QueryClient } from '@tanstack/react-query';
import { TrackingCategory } from '@tn/foundation/entities/tracking/const';
import { TrackerEngine } from '@tn/foundation/entities/tracking/lib/tracker-engine';
import { ACTIVATE_LOCALSTORAGE_KEYS } from '@tn/foundation/utils/constants/signup';
import {
    APP_LOG_TYPE,
    PAGE_TYPES,
    TRACK_LINK_NAMES,
    USER_LOGIN_TYPE,
} from '@tn/foundation/utils/constants/tracking';

import { devLog } from '../../utils/devLog';
import { getUser } from '../helpers';

type TrackSignInFailureParams = {
    passIsVisible?: string | boolean;
    appLogText: string;
    appLogCode: string;
};

type TrackSignInSuccessParams = {
    vendorCode?: string | null;
    /** Legacy parity: 'rendezvous' when the sign-in continues a device-activation flow. */
    userLoginType?: USER_LOGIN_TYPE;
};

const normalizePassIsVisible = (value?: string | boolean): '0' | '1' | undefined => {
    if (typeof value === 'boolean') return value ? '1' : '0';
    return value as '0' | '1' | undefined;
};

/**
 * Tracks a successful sign in event.
 * Called when the user successfully signs in on web.
 */
export const trackSignInSuccess = ({
    vendorCode,
    userLoginType,
}: TrackSignInSuccessParams = {}): void => {
    try {
        const ottActivationPartner =
            typeof window !== 'undefined'
                ? localStorage.getItem(ACTIVATE_LOCALSTORAGE_KEYS.LOCAL_ORIGINATING_CLIENTID_KEY)
                : null;
        const activationDeviceFamily =
            typeof window !== 'undefined'
                ? localStorage.getItem(ACTIVATE_LOCALSTORAGE_KEYS.LOCAL_PLATFORMCODE_KEY)
                : null;
        const activationShortCode =
            typeof window !== 'undefined'
                ? localStorage.getItem(ACTIVATE_LOCALSTORAGE_KEYS.LOCAL_ACTIVATION_CODE)
                : null;

        void TrackerEngine.update({
            type: TRACK_LINK_NAMES.TRACK_LOGIN_SUCCESS,
            category: TrackingCategory.App.INTERACT,
            detail: {
                context: {
                    pageType: PAGE_TYPES.SIGN_IN,
                    ...(vendorCode ? { vendorCode } : {}),
                    ...(userLoginType ? { userLoginType } : {}),
                    ...(ottActivationPartner ? { ottActivationPartner } : {}),
                    ...(activationDeviceFamily ? { activationDeviceFamily } : {}),
                    ...(activationShortCode ? { activationShortCode } : {}),
                },
            },
        });
    } catch (e) {
        // Don't allow tracking failures to break user flow
        devLog('[trackSignInSuccess] error', () => e);
    }
};

/**
 * Tracks a sign in failure event.
 * Called when the user submits the sign in form and the login fails.
 */
export const trackSignInFailure = ({
    passIsVisible,
    appLogText,
    appLogCode,
}: TrackSignInFailureParams): void => {
    try {
        void TrackerEngine.update({
            type: TRACK_LINK_NAMES.TRACK_APP_LOG,
            category: TrackingCategory.App.LOG,
            detail: {
                context: {
                    pageType: PAGE_TYPES.SIGN_IN,
                    passIsVisible: normalizePassIsVisible(passIsVisible) ?? '0',
                    appLogType: APP_LOG_TYPE.LOGIN_FAIL,
                    appLogText,
                    appLogCode: appLogCode || 'No code provided',
                },
            },
        });
    } catch (e) {
        // Don't allow tracking failures to break user flow
        devLog('[trackSignInFailure] error', () => e);
    }
};

/**
 * Tracks a successful billing-partner bind after sign-in — legacy
 * trackPartnerBindingSuccess parity (purchase-complete event).
 */
export const trackPartnerBindSuccess = (queryClient: QueryClient): void => {
    try {
        const packageInfo = getUser(queryClient)?.userFullProfile?.cbsPackageInfo?.[0];
        TrackerEngine.update({
            type: TRACK_LINK_NAMES.TRACK_PARTNER_BIND_SUCCESS,
            category: TrackingCategory.App.INTERACT,
            detail: {
                context: {
                    pageType: PAGE_TYPES.SIGN_IN,
                    purchaseEventOrderComplete: '1',
                    purchaseQuantity: '1',
                    ...(packageInfo?.packageCode
                        ? { purchaseProduct: packageInfo.packageCode }
                        : {}),
                    ...(packageInfo?.productName
                        ? { purchaseProductName: packageInfo.productName }
                        : {}),
                    ...(packageInfo?.planType ? { productPricingPlan: packageInfo.planType } : {}),
                    ...(packageInfo?.packageSource
                        ? { packageSource: packageInfo.packageSource }
                        : {}),
                    ...(packageInfo?.packageStatus
                        ? { packageStatus: packageInfo.packageStatus }
                        : {}),
                },
            },
        });
    } catch (e) {
        // Don't allow tracking failures to break user flow
        devLog('[trackPartnerBindSuccess] error', () => e);
    }
};

/**
 * Tracks a failed billing-partner bind after sign-in — legacy
 * trackPartnerBindingFail parity.
 */
export const trackPartnerBindFailure = ({
    errorCode,
    message,
}: {
    errorCode: string;
    message?: string;
}): void => {
    try {
        TrackerEngine.update({
            type: TRACK_LINK_NAMES.TRACK_APP_LOG,
            category: TrackingCategory.App.LOG,
            detail: {
                context: {
                    pageType: PAGE_TYPES.BIND_FAILURE,
                    appLogType: APP_LOG_TYPE.APP_LOG_ERROR_TYPE,
                    appLogText: message ?? errorCode,
                    appLogCode: errorCode,
                },
            },
        });
    } catch (e) {
        // Don't allow tracking failures to break user flow
        devLog('[trackPartnerBindFailure] error', () => e);
    }
};
